import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { isUserAgentMobile } from 'common/util/isUserAgentMobileApp';

import { stateOptionItems } from 'common/util/stateCodes';

import pointInsidePolygon from 'common/util/pointInsidePolygon';
import addressStreetSuffixes from 'common/addressStreetSuffixes';
import capitalize from 'common/util/capitalize';

import { hasRoleLandlord } from 'common/userRoles';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_KEY,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_NO_KEY,
  MARKETING_PARTNER_LISTING_CATEGORY_MOVE_IN_READY,
} from 'common/marketingPartnerListingConstants';

import { cleanMarketingPartnerListingFormValues } from 'common/marketingPartnerListingHelpers';

import {
  LISTING_ERROR_DUPLICATE_ADDRESS,
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_SERVICE_TYPE_SUNRISE,
  SUNRISE_LISTING_OCCUPANT_TYPES,
  LISTING_PROPERTY_OWNER_INSTITUTION,
  LISTING_SUNRISE_PROPERTY_OWNER_OPTION_ITEMS,
  LISTING_PROPERTY_OWNER_INDIVIDUAL,
} from 'common/listingConstants';

import {
  makeListingAddressStreetDisplay,
  makeListingFullAddressString,
} from 'common/listingHelpers';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
} from 'common/dataTableConstants';

import log from 'client/lib/log';

import { extractListingAddressFieldsFromGooglePlace } from 'client/lib/googlePlaces';

import postWithState from 'client/actions/postWithState';
import MobileApp from 'client/actions/MobileApp';

import { hideFieldIf, showFieldIf } from 'client/helpers/formikHelpers';
import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
} from 'client/components/SrMultiStepForm';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import { actionDataTableReset } from 'client/helpers/dataTables';
import SrPageFooter from './SrPageFooter';
import SrPartnerMarketListingFormStepSimple from './SrPartnerMarketListingFormStepSimple';
import SrOccupancy from './SrOccupancy';
import SrAddressInput from './SrAddressInput';
import SrMarketingItemsSunrise from './SrMarketingItemsSunrise';
import SrSunrisePropertySummary from './SrSunrisePropertySummary';

function makeFormStepsGetterFunc({
  formIsSubmitting,
  partner,
  requiresPhotos,
  requiresMarketingItems,
  isAlreadyActive,
}) {
  return ({ values }) => {
    const addressStreetDisplay = makeListingFullAddressString(values);

    const {
      referralFeeEnabled,
      informationCompletenessEnabled,
      additionalMarketingDocsEnabled,
    } = partner.experienceConfiguration;

    const steps = [
      {
        id: 'address',
        component: SrAddressInput,
        ui: {
          navbarTitle: 'Market Property',
          pageHeader: 'What property are you listing for lease?',
          showHelpCards: true,
        },
        fieldIds: [
          'addressStreetDisplay',
          'apartmentUnit',
          'addressStreetNumber',
          'addressStreetName',
          'addressCity',
          'addressStateCode',
          'addressZip',
          'locationLatitude',
          'locationLongitude',
        ],
        serverValidations: [
          {
            name: 'addressIsUnique',
            args: [
              'apartmentUnit',
              'addressStreetNumber',
              'addressStreetName',
              'addressCity',
              'addressStateCode',
              'addressZip',
              'partnerId',
            ],
            errorMessage: LISTING_ERROR_DUPLICATE_ADDRESS,
            fieldIdError: 'apartmentUnit',
          },
        ],
      },
      {
        id: 'reassignation',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Listing Re-Assigned?',
        },
        fieldIds: [
          'isAlreadyActive',
          'originalGoLiveDate',
          'reassignedByPartnerDate',
          'requiresMarketingItems',
          'requiresPhotos',
          'reassignationAvailableDate',
          'reassignationActiveStatus',
          'cantTourFlag',
          'note',
        ],
      },
      {
        id: 'availability',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'When will this property be accessible?',
        },
        fieldIds: ['availableForSetupDate', 'releaseToMarketDate', 'availableDate'],
      },
      {
        id: 'price',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How should we price the unit?',
        },
        fieldIds: ['shouldRunCMA', 'price'],
      },
      {
        id: 'propertyOwner',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Who owns the property?',
        },
        fieldIds: [
          'propertyOwnerType',
          'propertyOwnerName',
          'propertyOwnerPhone',
          'propertyOwnerEmail',
          'propertySecondOwnerName',
          'propertySecondOwnerPhone',
          'propertySecondOwnerEmail',
        ],
      },
      {
        id: 'referralFee',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Sunroom leasing commission',
        },
        fieldIds: ['leaseReferralFee'],
      },
      {
        id: 'occupant',
        component: SrOccupancy,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Who occupies the property?',
        },
        fieldIds: lodashCompact([
          'occupantType',
          'vacantDate',
          'occupantName',
          'occupantPhone',
          'occupantEmail',
        ]),
      },
      {
        id: 'marketingItems',
        component: SrMarketingItemsSunrise,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'What marketing items do you want?',
        },
        fieldIds: lodashCompact([
          'yardSignAllowed',
          'requireSelfShowingHardware',
          'requirePhotos',
          'availableForPhotoDate',
          'availableForPhotoDateSchedule',
          'availableForPhotoContactName',
          'availableForPhotoContactPhone',
          'photosSpecialInstructions',
        ]),
      },
      {
        id: 'access',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How can Sunroom access the property?',
        },
        fieldIds: ['accessMethod', 'keyPickupDescription', 'lockboxCode', 'gateCode', 'alarmCode'],
      },
      {
        id: 'uploadMarketingDocs',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: 'Upload marketing docs',
          pageHeader: 'Upload marketing docs',
        },
        fieldIds: ['additionalMarketingDocs'],
      },
      {
        id: 'extraMarketingInfo',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Extra marketing info',
        },
        fieldIds: ['informationCompleteness', 'marketingInfoRequiredFromOwner'],
      },
      {
        id: 'summary',
        component: SrSunrisePropertySummary,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Summary',
          controlFooterText: ' ',
          controlComponent: SrPageFooter,
          controlButtonText: formIsSubmitting
            ? 'Submitting Listing...'
            : 'Submit Property for Setup',
        },
        fieldIds: [],
      },
    ];

    const stepsById = steps.reduce(
      (memo, step) => ({
        ...memo,
        [step.id]: step,
      }),
      {},
    );

    const partnerSteps = lodashCompact([
      'address',
      'reassignation',
      'propertyOwner',
      (!isAlreadyActive || requiresMarketingItems || requiresPhotos) && 'availability',
      'price',
      'occupant',
      (!isAlreadyActive || requiresMarketingItems || requiresPhotos) && 'marketingItems',
      'access',
      additionalMarketingDocsEnabled && 'uploadMarketingDocs',
      informationCompletenessEnabled && !isAlreadyActive && 'extraMarketingInfo',
      referralFeeEnabled && 'referralFee',
      'summary',
    ]);

    const formSteps = partnerSteps.map((stepId) => ({
      ...stepsById[stepId],
      ui: {
        theme: SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
        controlComponent: SrPageFooter,
        controlFooterText: ' ',
        ...stepsById[stepId].ui,
      },
    }));

    return formSteps;
  };
}

function makeFormFieldsGetterFunc({
  isMobile,
  googleAddressMatches,
  onGoogleAddressChange,
  onFindGoogleListings,
  onOcuppantTypeChange,
  marketsList,
  partner,
  onChangeRequiresMarketingItems,
  onChangeIsAlreadyActive,
  onChangeRequiresPhotos,
}) {
  return (formikBag) => {
    const { values, setFieldValue } = formikBag;

    const shouldShowOccupantFields =
      values.occupantType && values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT;

    const shouldShowScheduleContact =
      (values.requirePhotos || values.requireVirtual3dTour) &&
      values.availableForPhotoDateSchedule === AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE;

    if (values.locationLongitude && values.locationLatitude) {
      for (const market of marketsList) {
        const point = [values.locationLongitude, values.locationLatitude];
        if (pointInsidePolygon(point, market.serviceZone.coordinates)) {
          break;
        }
      }
    }

    return [
      {
        id: 'manualAddressInput',
        type: 'hidden',
      },
      hideFieldIf(values.manualAddressInput, {
        id: 'addressStreetDisplay',
        title: 'Address',
        type: 'autocomplete',
        validations: lodashCompact([
          'required',
          (!values.locationLatitude || !values.locationLongitude) && 'useManualAddressInput',
        ]),
        properties: {
          placeholder: 'Write address...',
          minCharMatchCount: 1,
          choices: googleAddressMatches.map(({ description }) => description),
          onSuggestionsFetchRequested: ({ value }) => {
            return onFindGoogleListings({ ...values, addressStreetDisplay: value });
          },
          onChange: onGoogleAddressChange,
          filterFunc: () => {
            return true;
          },
        },
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetNumber',
        type: 'short_text',
        title: 'Street Number',
        properties: {
          placeholder: 'Street Number',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetName',
        type: 'short_text',
        title: 'Street Name',
        properties: {
          placeholder: 'Street Name',
        },
        validations: ['required'],
      }),
      {
        id: 'addressStreetSuffix',
        type: 'hidden',
      },
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressCity',
        type: 'short_text',
        title: 'City',
        properties: {
          placeholder: 'City',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStateCode',
        type: 'select',
        title: 'State',
        properties: {
          choices: stateOptionItems,
          enableResetOption: true,
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressZip',
        type: 'number',
        title: 'ZIP Code',
        properties: {
          placeholder: 'ZIP Code',
        },
        validations: ['required'],
      }),
      {
        id: 'locationLatitude',
        type: 'hidden',
      },
      {
        id: 'locationLongitude',
        type: 'hidden',
      },
      {
        id: 'apartmentUnit',
        title: 'Unit #',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Unit #',
        },
      },
      {
        id: 'isAlreadyActive',
        title: `Is this listing already active? (${partner.displayName} reassigned it to Sunroom)`,
        type: 'yes_no',
        labels: {
          yes: "Yes, it's active",
          no: 'No, we need to set it up',
        },
        properties: {
          onChange: (value) => onChangeIsAlreadyActive(value),
        },
        validations: ['required'],
      },
      showFieldIf(values.isAlreadyActive, {
        id: 'originalGoLiveDate',
        title: 'When did this listing go live originally?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
        },
        validations: ['required'],
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'reassignedByPartnerDate',
        title: `When did ${partner.displayName} re-assign this to us?`,
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
        },
        validations: ['required'],
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'reassignationAvailableDate',
        title: 'Available date (may be in the past)',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          onChange: (reassignationAvailableDate) => {
            setFieldValue('availableDate', reassignationAvailableDate);
          },
        },
        validations: ['required'],
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'requiresMarketingItems',
        title: 'Do marketing items need to be installed?',
        type: 'yes_no',
        validations: ['required'],
        properties: {
          onChange: (value) => onChangeRequiresMarketingItems(value),
        },
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'requiresPhotos',
        title: 'Do marketing photos need to be ordered?',
        type: 'yes_no',
        validations: ['required'],
        properties: {
          onChange: onChangeRequiresPhotos,
        },
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'reassignationActiveStatus',
        title: 'Live on Tenant Turner',
        type: 'yes_no',
        validations: ['required'],
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'cantTourFlag',
        title: 'Tourable on Tenant Turner?',
        type: 'yes_no',
        properties: {
          oppositeBoolean: true,
        },
        validations: ['required'],
      }),
      showFieldIf(values.isAlreadyActive, {
        id: 'note',
        title: 'Listing Notes (Optional)',
        properties: {
          placeholder: 'Write notes here...',
        },
        type: 'long_text',
      }),
      {
        id: 'occupantType',
        title: 'Occupant Status',
        type: 'multiple_choice',
        properties: {
          choices: SUNRISE_LISTING_OCCUPANT_TYPES.map((listingType) => ({
            label: listingType,
            value: listingType,
          })),
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          onChange: onOcuppantTypeChange,
        },
        validations: ['required'],
      },
      hideFieldIf(!shouldShowOccupantFields, {
        id: 'occupantName',
        title: 'Occupant full name',
        type: 'short_text',
        properties: {
          placeholder: 'Occupant full name',
        },
        validations: ['required'],
      }),
      hideFieldIf(!shouldShowOccupantFields, {
        id: 'occupantPhone',
        title: 'Occupant phone',
        type: 'phone',
        properties: {
          placeholder: 'Occupant phone',
          pattern: '[0-9]*',
        },
        validations: ['required', 'phone'],
      }),
      hideFieldIf(!shouldShowOccupantFields, {
        id: 'occupantEmail',
        title: 'Occupant email',
        type: 'email',
        properties: {
          placeholder: 'Occupant email',
        },
        validations: ['email'],
      }),
      hideFieldIf(!shouldShowOccupantFields, {
        id: 'vacantDate',
        title: 'Scheduled move-out date',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
        },
        validations: ['required'],
      }),
      {
        id: 'leaseReferralFee',
        type: 'currency',
        title: 'Sunroom leasing commission',
        validations: ['required'],
        properties: {
          placeholder: 'Fee amount',
        },
      },
      showFieldIf(!values.isAlreadyActive || values.requiresMarketingItems, {
        id: 'availableForSetupDate',
        title: 'Earliest day Sunroom can install equipment?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      }),
      {
        id: 'releaseToMarketDate',
        title: 'What date would you like the listing to be put on the market?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      },
      hideFieldIf(values.isAlreadyActive, {
        id: 'availableDate',
        title: 'Earliest a renter can move in?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      }),
      {
        id: 'accessMethod',
        title: 'Key Access',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          choices: [
            {
              label: 'Key is at the property',
              value: PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_KEY,
            },
            {
              label: 'Key is not at the property',
              value: PROPERTY_SETUP_ACCESS_METHOD_TYPE_SUNRISE_NO_KEY,
            },
          ],
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '400px',
        },
        validations: ['required'],
      },
      {
        id: 'keyPickupDescription',
        title: 'How can Sunroom get the key?',
        type: 'long_text',
        properties: {
          placeholder: 'Instructions for Sunroom to get the key...',
        },
        validations: ['required'],
      },
      {
        id: 'lockboxCode',
        title: 'Combo Lockbox code',
        type: 'short_text',
        properties: {
          placeholder: '1234',
        },
        validations: values.keyPickupDescription ? null : ['required'],
      },
      {
        id: 'gateCode',
        title: 'Gate Code',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: '1234#',
        },
      },
      {
        id: 'alarmCode',
        title: 'Alarm Code',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: '*9876#',
        },
      },
      {
        id: 'additionalMarketingDocs',
        title: 'Please provide any additional marketing documents here',
        label: '(optional)',
        type: 'file_multi',
        properties: {
          filePath: '/listing-uploads/additional-marketing-docs/{uuid}',
          placeholder: 'Upload marketing docs',
          value: values.additionalMarketingDocs,
          hidePreview: true,
          replaceFields: true,
        },
      },
      {
        id: 'informationCompleteness',
        title: 'Do you have all of the marketing information from the owner?',
        // eslint-disable-next-line max-len
        subtitle:
          'Such as pet policy, who is responsible for the lawn, appliances included, appliances, etc.',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          choices: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No, ask owner for me',
              value: false,
            },
          ],
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '400px',
        },
        validations: ['required'],
      },
      showFieldIf(values.informationCompleteness === false, {
        id: 'marketingInfoRequiredFromOwner',
        title: 'What info do you need?',
        type: 'long_text',
        properties: {
          placeholder: 'Write information you want from owner...',
        },
        validations: ['required'],
      }),
      {
        id: 'shouldRunCMA',
        title: 'Has the owner already agreed to a rent price?',
        type: 'multiple_choice',
        properties: {
          choices: [
            {
              label: 'Yes',
              value: false,
            },
            {
              label: 'No, run a CMA',
              value: true,
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '410px',
        },
        validations: ['required'],
      },
      hideFieldIf(values.shouldRunCMA, {
        id: 'price',
        title: 'Rent price',
        type: 'currency',
        properties: {
          placeholder: 'Rent price',
        },
        validations: values.shouldRunCMA ? null : ['required'],
      }),
      {
        id: 'propertyOwnerType',
        title: 'Owner type',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          choices: LISTING_SUNRISE_PROPERTY_OWNER_OPTION_ITEMS,
          autoFitWidth: '400px',
          onChange: (propertyOwnerType) => {
            setFieldValue('propertyOwnerType', propertyOwnerType);
            setFieldValue('propertyOwnerName', '');
            setFieldValue('propertyOwnerPhone', '');
            setFieldValue('propertyOwnerEmail', '');
            setFieldValue('propertySecondOwnerName', '');
            setFieldValue('propertySecondOwnerPhone', '');
            setFieldValue('propertySecondOwnerEmail', '');
          },
        },
        validations: ['required'],
      },
      showFieldIf(!!values.propertyOwnerType, {
        id: 'propertyOwnerName',
        title:
          values.propertyOwnerType === LISTING_PROPERTY_OWNER_INSTITUTION
            ? 'Institution name'
            : 'Owner full name',
        type: 'short_text',
        properties: {
          placeholder:
            values.propertyOwnerType === LISTING_PROPERTY_OWNER_INSTITUTION
              ? 'Institution name'
              : 'Owner full name',
        },
        validations: ['required'],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL, {
        id: 'propertyOwnerPhone',
        title: 'Owner phone',
        type: 'phone',
        properties: {
          placeholder: 'Owner phone',
        },
        validations: ['required', 'phone'],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL, {
        id: 'propertyOwnerEmail',
        title: 'Owner email',
        type: 'email',
        properties: {
          placeholder: 'Owner email',
        },
        validations: ['required', 'email'],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL, {
        id: 'propertySecondOwnerName',
        title: 'Second Owner full name (optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Second Owner full name',
        },
        validations: [],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL, {
        id: 'propertySecondOwnerPhone',
        title: 'Second Owner phone (optional)',
        type: 'phone',
        properties: {
          placeholder: 'Second Owner phone',
        },
        validations: ['phone'],
      }),
      showFieldIf(values.propertyOwnerType === LISTING_PROPERTY_OWNER_INDIVIDUAL, {
        id: 'propertySecondOwnerEmail',
        title: 'Second Owner email (optional)',
        type: 'email',
        properties: {
          placeholder: 'Second Owner email',
        },
        validations: ['email'],
      }),
      hideFieldIf(values.isAlreadyActive && !values.requiresMarketingItems, {
        id: 'yardSignAllowed',
        title: 'Yard Sign allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(values.isAlreadyActive && !values.requiresMarketingItems, {
        id: 'requireSelfShowingHardware',
        title: 'Do you want to install self-showing hardware?',
        subtitle: 'Tours only available when property is vacant.',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(values.isAlreadyActive && !values.requiresPhotos, {
        id: 'requirePhotos',
        title: 'Want Sunroom to take photos?',
        subtitle: 'Inside photos only available for vacant properties.',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(!values.requirePhotos, {
        id: 'availableForPhotoDate',
        title: 'Earliest day Sunroom can take photos?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.requirePhotos, {
        id: 'photosSpecialInstructions',
        title: 'Special instructions for taking photos?',
        titleSecondary: '(optional)',
        subtitle: 'Instructions are sent directly to the photographer.',
        type: 'long_text',
        properties: {
          placeholder: 'Write special instructions for photos...',
        },
      }),
      hideFieldIf(!values.requirePhotos, {
        id: 'availableForPhotoDateSchedule',
        title: 'Can Sunroom go take photos any time during the day?',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          choices: AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '250px',
        },
        validations: ['required'],
      }),
      hideFieldIf(!shouldShowScheduleContact, {
        id: 'availableForPhotoContactName',
        title: 'Scheduling contact full name',
        properties: {
          placeholder: 'Scheduling contact full name',
        },
        type: 'short_text',
        validations: ['required'],
      }),
      hideFieldIf(!shouldShowScheduleContact, {
        id: 'availableForPhotoContactPhone',
        title: 'Scheduling contact phone',
        properties: {
          placeholder: 'Scheduling contact phone',
          pattern: '[0-9]*',
        },
        type: 'phone',
        validations: ['required', 'phone'],
      }),
      {
        id: 'partnerId',
        type: 'hidden',
      },
      {
        id: 'marketingPartnerListingId',
        type: 'hidden',
      },
      {
        id: 'listingId',
        type: 'hidden',
      },
      {
        id: 'listingAgreementSignedBy',
        type: 'hidden',
      },
      {
        id: 'services',
        type: 'hidden',
      },
    ];
  };
}

function makeLandlordMatchString({ name, phone, email }) {
  return `${name} - ${phone} - ${email}`;
}

class SrPartnerMarketSunriseListingForm extends Component {
  static propTypes = {
    onBack: PropTypes.func.isRequired,
    partnerId: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    partner: PropTypes.object,
    userAgent: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    helpCards: PropTypes.array,
    marketsList: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      googleAddressMatches: [],
      landlordMatches: [],
      shouldShowSuccessModal: false,
      shouldShowLoadingScreen: false,
      formIsSubmitting: false,
      successModalListingId: null,
      requiresMarketingItems: false,
      requiresPhotos: false,
      isAlreadyActive: false,
    };
    this._onFindGoogleListings = this._onFindGoogleListings.bind(this);
    this.multiStepFormRef = React.createRef();
  }

  _onSubmit = (formValues) => {
    this.setState({
      formIsSubmitting: true,
    });
    const { dispatch } = this.props;
    const updates = cleanMarketingPartnerListingFormValues(formValues);
    updates.addressStreetDisplay = makeListingAddressStreetDisplay(formValues);
    if (updates.addressStreetName) {
      const splitAddress = updates.addressStreetName.split(' ');
      const capitalizedLastWord = capitalize(splitAddress[splitAddress.length - 1].toLowerCase());
      const includesSuffix = addressStreetSuffixes.includes(capitalizedLastWord);
      if (includesSuffix) {
        updates.addressStreetSuffix = capitalizedLastWord;
        updates.addressStreetName = splitAddress.slice(0, -1).join(' ');
      }
    }
    updates.status = MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS;
    updates.category = MARKETING_PARTNER_LISTING_CATEGORY_MOVE_IN_READY;
    updates.cantTourFlag = updates.cantTourFlag || false;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
      payload: setNullEmptyValues({
        updates: updates,
      }),
      onSuccess: (res) => {
        this.setState({
          shouldShowSuccessModal: true,
          formIsSubmitting: false,
        });

        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
        });

        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
        });

        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
        });

        this.setState({
          successModalListingId: parseInt(res.listingId, 10),
        });
      },
      onError: (error) => {
        log.error(error);
      },
    });
  };

  _onSuccessModalClick = () => {
    const { partner, history } = this.props;
    const { successModalListingId } = this.state;

    const redirectUrl = `/partner/${partner.slug}/listing/${successModalListingId}?scrollTo=setupChecklist'`;
    history.push(redirectUrl);
  };

  _onFindGoogleListings(formValues) {
    const { dispatch } = this.props;
    if (!formValues.addressStreetDisplay) {
      this.setState({
        googleAddressMatches: [],
      });
      return;
    }

    dispatch(postWithState, {
      queryId: formValues.addressStreetDisplay,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: formValues.addressStreetDisplay,
        limitToTexas: false,
      },
    }).then((autocompletePredictions) => {
      this.setState({
        googleAddressMatches: autocompletePredictions,
      });
    });
  }

  _onChangeRequiresMarketingItems = (value) => {
    this.setState({
      requiresMarketingItems: value,
    });
  };

  _onChangeIsAlreadyActive = (value) => {
    this.setState({
      isAlreadyActive: value,
    });
  };

  _onChangeRequiresPhotos = (value) => {
    this.setState({
      requiresPhotos: value,
    });
  };

  _onGoogleAddressChange = ({ newValue, formikBag }) => {
    const { googleAddressMatches } = this.state;

    const selectedMatch = googleAddressMatches?.find(({ description }) => description === newValue);
    if (selectedMatch?.place_id) {
      this._loadMatchAddressIntoForm({
        placeId: selectedMatch.place_id,
        formikBag,
      });
    } else {
      formikBag.setFieldValue('addressStreetNumber', '');
      formikBag.setFieldValue('addressStreetName', '');
      formikBag.setFieldValue('addressCity', '');
      formikBag.setFieldValue('addressStateCode', '');
      formikBag.setFieldValue('addressZip', '');
      formikBag.setFieldValue('locationLatitude', '');
      formikBag.setFieldValue('locationLongitude', '');
    }
  };

  _loadMatchAddressIntoForm = ({ placeId, formikBag }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
      payload: {
        placeId,
      },
    }).then((googlePlace) => {
      const listingMatch = extractListingAddressFieldsFromGooglePlace(googlePlace);
      if (listingMatch) {
        formikBag.setFieldValue('addressStreetNumber', listingMatch.addressStreetNumber);
        formikBag.setFieldValue('addressStreetName', listingMatch.addressStreetName);
        formikBag.setFieldValue('addressCity', listingMatch.addressCity);
        formikBag.setFieldValue('addressStateCode', listingMatch.addressState);
        formikBag.setFieldValue('addressZip', listingMatch.addressZip);
        formikBag.setFieldValue(
          'locationLatitude',
          parseFloat(listingMatch.locationLatitude) || null,
        );
        formikBag.setFieldValue(
          'locationLongitude',
          parseFloat(listingMatch.locationLongitude) || null,
        );
      }
    });
  };

  _onOcuppantTypeChange = (occupantType) => {
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    formikBag.setFieldValue('occupantType', occupantType);
    formikBag.setFieldValue('occupantName', '');
    formikBag.setFieldValue('occupantEmail', '');
    formikBag.setFieldValue('occupantPhone', '');
  };

  _askForPushNotifications = () => {
    this.props.dispatch(MobileApp.askForPushNotifications);
  };

  render() {
    const {
      currentUser,
      onBack,
      partner,
      partnerId,
      propertyAttributes = [],
      dispatch,
      userAgent,
      helpCards,
      marketsList,
    } = this.props;

    const {
      googleAddressMatches,
      landlordMatches,
      shouldShowSuccessModal,
      shouldShowLoadingScreen,
      formIsSubmitting,
      successModalListingId,
      requiresMarketingItems,
      requiresPhotos,
      isAlreadyActive,
    } = this.state;

    const isMobile = isUserAgentMobile(userAgent);
    const isLandlord = hasRoleLandlord(currentUser.roles);
    const getFormSteps = makeFormStepsGetterFunc({
      formIsSubmitting,
      partner,
      requiresMarketingItems,
      requiresPhotos,
      isAlreadyActive,
    });

    const getFormFields = makeFormFieldsGetterFunc({
      isLandlord,
      isMobile,
      googleAddressMatches: googleAddressMatches,
      onGoogleAddressChange: this._onGoogleAddressChange,
      onChangeRequiresMarketingItems: this._onChangeRequiresMarketingItems,
      onChangeIsAlreadyActive: this._onChangeIsAlreadyActive,
      onChangeRequiresPhotos: this._onChangeRequiresPhotos,
      onFindGoogleListings: this._onFindGoogleListings,
      landlordSuggestionChoices: landlordMatches.map(makeLandlordMatchString),
      onOcuppantTypeChange: this._onOcuppantTypeChange,
      onAccessMethodChange: this._onAccessMethodChange,
      onOpenSupraCbsModal: this._onOpenSupraCbsModal,
      onOpenSunlockSelfTouringModal: this._onOpenSunlockSelfTouringModal,
      marketsList,
      partner,
    });

    if (shouldShowLoadingScreen) {
      return <div className="loading-gif" />;
    }

    const defaultValues = {
      partnerId: partnerId,
      services: [LISTING_SERVICE_TYPE_SUNRISE],
      ...propertyAttributes.reduce(
        (memo, propertyAttribute) => ({
          ...memo,
          [propertyAttribute.name]: propertyAttribute.attrs.value,
        }),
        {},
      ),
    };

    return (
      <>
        <SrMultiStepForm
          ref={this.multiStepFormRef}
          getFormSteps={getFormSteps}
          getFormFields={getFormFields}
          onBack={onBack}
          onSubmit={this._onSubmit}
          defaultValues={defaultValues}
          confirmLeave={!shouldShowSuccessModal}
          dispatch={dispatch}
          partner={partner}
          helpCards={helpCards}
          validateOnClickNext
          formClassNames="marketProperty"
        />
        {successModalListingId && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Listing Submittted!"
            message={
              <SrBox maxWidth="296px">
                <SrBox margin="0 auto">
                  <SrText weight="800" size="16" color="blueGreycliff">
                    What’s next?
                  </SrText>
                </SrBox>
                {'Sunroom will setup the listing to how you specified. You can follow ' +
                  'the progress on the listing page in the “Setup Checklist.”'}
              </SrBox>
            }
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            buttonText="Ok, thanks"
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            onButtonClick={this._onSuccessModalClick}
          />
        )}
      </>
    );
  }
}

SrPartnerMarketSunriseListingForm.displayName = 'SrPartnerMarketSunriseListingForm';

export default SrPartnerMarketSunriseListingForm;
